import { useEffect, useState } from 'react';
import { AppPageHeader, Group, InfiniteTable } from '@/components/_common';
import { quantityFormatter } from '@/utils/number';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { COLUMNS_WIDTH, getTableScrollX } from '@/utils/table';
import { Button } from 'antd';
import { ColumnsType } from 'antd/lib/table/interface';
import moment from 'moment';
import { useIntl } from 'react-intl';

import StatusTag from '@/components/_common/StatusTag';
import { useDebounce } from '@/hooks';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import usePageSearchHistory from '@/hooks/pageSearchHistory';
import EyeIcon from '@/components/_common/EyeIcon';
import BillToReceiveMigrationConsultDetailsModal from '../BillToReceiveMigrationConsultDetailsModal';
import BillToReceiveMigrationConsultExtraFilters from '../BillToReceiveMigrationConsultExtraFilters';
import { useFinancialConsultationReceivableList } from '@/core/domain/financial/Consultation/consultation.hooks';
import {
  FinancialConsultInstallmentSituation,
  FinancialConsultInstallmentSituationType,
  FinancialConsultReceivable,
  FinancialConsultReceivableListRequest,
} from '@/core/domain/financial/Consultation/consultation.types';
import BillToReceiveMigrationConsultBind from '../BillToReceiveMigrationConsultBind';
import MigrationInstallmentBinded from '../../MigrationInstallmentBinded';

export default function BillToReceiveMigrationConsultList() {
  const intl = useIntl();
  const [installmentReceivableSelected, setInstallmentReceivableSelected] =
    useState<FinancialConsultReceivable>();

  const [queryParams, setQueryParams] =
    useState<FinancialConsultReceivableListRequest>({
      order: 'DESC',
      ...convertParams<FinancialConsultReceivableListRequest>({
        search: 'string',
        code: 'string',
        companyId: 'number',
        debtor: 'string',
        description: 'string',
        dueDate: 'string',
        payDate: 'string',
        emissionDate: 'string',
        situation: 'string',
        installmentValue: 'number',
        documentNumber: 'string',
        order: 'string',
        observation: 'string',
      }),
    });

  const queryDebounce = useDebounce(queryParams, 500);
  usePersistSearchParams(queryDebounce);

  const { setLocation } = usePageSearchHistory();
  useEffect(setLocation, [queryDebounce]);

  const installmentsConsultList =
    useFinancialConsultationReceivableList(queryDebounce);

  const installments = flatInfiniteData(installmentsConsultList.data);

  const columns: ColumnsType<FinancialConsultReceivable> = [
    {
      dataIndex: 'code',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.code',
      }),
      width: 140,
      key: 'code',
      render: (_, row) => `${row.code}-${row.installmentNumber}`,
    },
    {
      dataIndex: 'companyName',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.companies',
      }),
      width: 220,
      key: 'company',
      ellipsis: true,
    },
    {
      dataIndex: 'documentNumber',
      title: 'Nº documento',
      width: 220,
      key: 'documentNumber',
      ellipsis: true,
    },
    {
      dataIndex: 'debtor',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.debtor',
      }),
      width: 220,
      key: 'debtor',
      ellipsis: true,
    },
    {
      dataIndex: 'description',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.description',
      }),
      width: COLUMNS_WIDTH.action2,
      key: 'description',
    },
    {
      dataIndex: 'documentType',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.documentType',
      }),
      width: COLUMNS_WIDTH.price,
      key: 'documentType',
    },
    {
      dataIndex: 'emissionDate',
      title: 'Dt. emissão',
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      width: 100,
      key: 'emissionDate',
    },
    {
      dataIndex: 'dueDate',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.dueDate',
      }),
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      width: 100,
      key: 'dueDate',
    },
    {
      dataIndex: 'payDate',
      title: 'Dt. pagamento',
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      width: 100,
      key: 'payDate',
    },
    {
      width: COLUMNS_WIDTH.price,
      dataIndex: 'situation',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.situation',
      }),
      key: 'situation',
      render: (situation: FinancialConsultInstallmentSituation) => {
        const situationType =
          FinancialConsultInstallmentSituationType[situation];
        return (
          <StatusTag label={situationType.label} color={situationType.color} />
        );
      },
    },
    {
      dataIndex: 'value',
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.value',
      }),
      render: (_, record) =>
        `R$ ${quantityFormatter(
          record.installmentValue + (record.discountIncrease ?? 0),
        )}`,
      width: COLUMNS_WIDTH.price,
      key: 'value',
    },
    {
      key: 'actions',
      title: intl.formatMessage({
        id: 'commons.list.header.action',
      }),
      render: (_, item) => (
        <Group position="center" gap={10}>
          <MigrationInstallmentBinded
            accountId={item.boundAccountConsultation.mainId}
            installmentId={item.boundAccountConsultation.mainInstallmentId}
            type="receivable"
          />

          <EyeIcon
            iconProps={{
              onClick: () => setInstallmentReceivableSelected(item),
            }}
          />

          <BillToReceiveMigrationConsultBind
            migrationId={item.accountConsultationId}
          />
        </Group>
      ),
      ellipsis: true,
      width: 80,
      fixed: 'right',
      align: 'center',
    },
  ];

  return (
    <>
      <AppPageHeader
        onSearch={(value) =>
          setQueryParams((prev) => ({ ...prev, search: value }))
        }
        searchValue={queryParams.search}
        headerActions={({ isShowingFilters }) =>
          isShowingFilters && (
            <Button
              type="primary"
              onClick={() => setQueryParams({ order: 'DESC' })}
            >
              {intl.formatMessage({ id: 'commons.clear.filters' })}
            </Button>
          )
        }
        extraFilters={
          <BillToReceiveMigrationConsultExtraFilters
            queryParams={queryParams}
            setQueryParams={setQueryParams}
          />
        }
      />

      <InfiniteTable<FinancialConsultReceivable>
        columns={columns}
        dataSource={installments}
        loading={installmentsConsultList.isFetching}
        rowKey={(row) => row.id}
        hasNextPage={installmentsConsultList.hasNextPage}
        fetchNextPage={installmentsConsultList.fetchNextPage}
        scroll={{ x: getTableScrollX(columns) }}
      />

      <BillToReceiveMigrationConsultDetailsModal
        open={!!installmentReceivableSelected}
        onCancel={() => setInstallmentReceivableSelected(undefined)}
        installmentData={installmentReceivableSelected}
      />
    </>
  );
}
